import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { 
  Save, 
  ArrowLeft, 
  Heart,
  Tag,
  Calendar,
  Smile,
  Frown,
  Meh
} from 'lucide-react';
import { JournalEntry } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';

export const JournalEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addEvent } = useAnalytics();
  const isEditing = id && id !== 'new';

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [entry, setEntry] = useState<Partial<JournalEntry>>({
    content: '',
    mood: 5,
    tags: [],
    date: new Date().toISOString().split('T')[0],
    sharedScope: 'private'
  });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (isEditing && id && user) {
      const fetchEntry = async () => {
        try {
          const entryDoc = await getDoc(doc(db, 'users', user.uid, 'journals', id));
          if (entryDoc.exists()) {
            const entryData = {
              id: entryDoc.id,
              ...entryDoc.data(),
              createdAt: entryDoc.data().createdAt?.toDate(),
              updatedAt: entryDoc.data().updatedAt?.toDate(),
              date: entryDoc.data().date || new Date().toISOString().split('T')[0]
            } as JournalEntry;
            setEntry(entryData);
          }
        } catch (error) {
          console.error('Error fetching journal entry:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchEntry();
    }
  }, [id, isEditing, user]);

  const handleSave = async () => {
    if (!user || !entry.content.trim()) return;

    setSaving(true);
    try {
      const entryData = {
        ...entry,
        uid: user.uid,
        updatedAt: new Date(),
        offlineDraft: false
      };

      if (isEditing && id) {
        await updateDoc(doc(db, 'users', user.uid, 'journals', id), entryData);
      } else {
        entryData.createdAt = new Date();
        await addDoc(collection(db, 'users', user.uid, 'journals'), entryData);
        await addEvent('journal.create');
      }

      navigate('/journal');
    } catch (error) {
      console.error('Error saving journal entry:', error);
      alert('Error saving entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !entry.tags?.includes(tagInput.trim())) {
      setEntry(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setEntry(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const getMoodEmoji = (mood: number) => {
    if (mood >= 8) return '😊';
    if (mood >= 6) return '🙂';
    if (mood >= 4) return '😐';
    if (mood >= 2) return '😟';
    return '😢';
  };

  const getMoodLabel = (mood: number) => {
    if (mood >= 9) return 'Excellent';
    if (mood >= 7) return 'Great';
    if (mood >= 5) return 'Good';
    if (mood >= 3) return 'Okay';
    if (mood >= 1) return 'Poor';
    return 'Very Poor';
  };

  const moodColors = {
    1: 'bg-red-500',
    2: 'bg-red-400',
    3: 'bg-orange-500',
    4: 'bg-yellow-500',
    5: 'bg-yellow-400',
    6: 'bg-green-400',
    7: 'bg-green-500',
    8: 'bg-green-600',
    9: 'bg-emerald-500',
    10: 'bg-emerald-600'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/journal')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Entry' : 'New Journal Entry'}
            </h1>
            <p className="text-gray-600 mt-1">
              Take a moment to reflect on your day
            </p>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || !entry.content?.trim()}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {saving ? <LoadingSpinner size="sm" color="white" /> : <Save className="w-5 h-5" />}
          <span>Save Entry</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Date */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Calendar className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">Date</label>
            </div>
            <input
              type="date"
              value={entry.date}
              onChange={(e) => setEntry(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              How was your day? What's on your mind?
            </label>
            <textarea
              value={entry.content}
              onChange={(e) => setEntry(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Write about your experiences, feelings, challenges, or victories today..."
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
            />
            <div className="mt-2 text-sm text-gray-500">
              {entry.content?.length || 0} characters
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Mood Tracker */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Heart className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">Mood Rating</label>
            </div>
            
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">{getMoodEmoji(entry.mood || 5)}</div>
              <div className="text-lg font-semibold text-gray-900">{entry.mood || 5}/10</div>
              <div className="text-sm text-gray-600">{getMoodLabel(entry.mood || 5)}</div>
            </div>

            <div className="space-y-2">
              <input
                type="range"
                min="1"
                max="10"
                value={entry.mood || 5}
                onChange={(e) => setEntry(prev => ({ ...prev, mood: Number(e.target.value) }))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #ef4444 0%, #f59e0b 50%, #10b981 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>Poor</span>
                <span>Okay</span>
                <span>Great</span>
              </div>
            </div>

            {/* Quick Mood Buttons */}
            <div className="grid grid-cols-3 gap-2 mt-4">
              <button
                onClick={() => setEntry(prev => ({ ...prev, mood: 2 }))}
                className={`p-2 rounded-lg border transition-colors ${
                  (entry.mood || 5) <= 3 ? 'bg-red-50 border-red-200' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Frown className="w-5 h-5 mx-auto text-red-500" />
                <div className="text-xs mt-1">Low</div>
              </button>
              <button
                onClick={() => setEntry(prev => ({ ...prev, mood: 5 }))}
                className={`p-2 rounded-lg border transition-colors ${
                  (entry.mood || 5) >= 4 && (entry.mood || 5) <= 6 ? 'bg-yellow-50 border-yellow-200' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Meh className="w-5 h-5 mx-auto text-yellow-500" />
                <div className="text-xs mt-1">Okay</div>
              </button>
              <button
                onClick={() => setEntry(prev => ({ ...prev, mood: 8 }))}
                className={`p-2 rounded-lg border transition-colors ${
                  (entry.mood || 5) >= 7 ? 'bg-green-50 border-green-200' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Smile className="w-5 h-5 mx-auto text-green-500" />
                <div className="text-xs mt-1">Good</div>
              </button>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Tag className="w-5 h-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">Tags</label>
            </div>

            <div className="flex space-x-2 mb-4">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                placeholder="Add a tag..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
              />
              <button
                onClick={addTag}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Add
              </button>
            </div>

            {entry.tags && entry.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {entry.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      className="ml-1 text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Quick Tag Suggestions */}
            <div className="mt-4">
              <p className="text-xs text-gray-500 mb-2">Quick tags:</p>
              <div className="flex flex-wrap gap-1">
                {['work', 'family', 'stress', 'gratitude', 'exercise', 'sleep'].map(tag => (
                  <button
                    key={tag}
                    onClick={() => {
                      if (!entry.tags?.includes(tag)) {
                        setEntry(prev => ({
                          ...prev,
                          tags: [...(prev.tags || []), tag]
                        }));
                      }
                    }}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Privacy */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Privacy Setting
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="privacy"
                  value="private"
                  checked={entry.sharedScope === 'private'}
                  onChange={(e) => setEntry(prev => ({ ...prev, sharedScope: e.target.value }))}
                  className="mr-2"
                />
                <span className="text-sm">Private (only you can see this)</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Your journal entries are always private and secure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
